// filebase.ts basic file handling class
// it is the base to create file system for access any online drive
// which need to extent this base class
// it model the FileSystem in javascript 


// import * as mime from 'mime-types';
export interface EntryMetadata {
    lastModified?: Date;
    created?: Date;
    lastModifiedBy?: string;
    createdBy?: string;
    version?: string;
    u_id?: string;
    size?: number;
}

export interface ExtendedField {
	name: string;
	value: string|number|Date;
	type?: 'string' | 'number' | 'date';
}

export interface PureMetadata {
    id?: string;
    name?: string;
    size?: number;
    webUrl?: string;
    contentType?: string;
    contentUrl?: string;
}
export const LddLinkFileExt = "ldd";
export abstract class BaseEntry {
    fullPath?: string;
    name: string;
    isFile: boolean;
    isDirectory: boolean;
    isLinkFile = false;
    fileSystem: FileSystem;
    id: string;
    createdBy?: string;
    modifiedDate?: string;
    createdDate?: string;
    size?: number;
    version?: number;

	extentedFieldSpec?: Array<string>; // provide what extra fields file system can support
    get ext(): string {
        return this.name.substring(this.name.lastIndexOf('.') === -1 ? this.name.length : this.name.lastIndexOf('.') + 1);
    }
    get justName():string {
        return this.name.substring(0,this.name.lastIndexOf('.') === -1 ? this.name.length : this.name.lastIndexOf('.'));
    }
    get actualExt(): string {
        let ext = this.ext;
        if (this.isLinkFile){
            ext = `${LddLinkFileExt}${ext}`;
        }
        return ext;
    }
    get actualName(): string {
        let name = this.name;
        if (this.isLinkFile){
            name = `${this.justName}.${this.actualExt}`;
        }
        return name;
    }

    get contentType(): string {
        var mimeType = "";
        const ext = this.ext.toLowerCase();
        switch (ext) {
            case "odt":
                mimeType = "application/vnd.oasis.opendocument.text";
                break;
            case "pdf":
                mimeType = "application/pdf";
                break;
            case "xml":
                // transfer or pdfing 
                mimeType = "text/xml";
                break;
            case "txt":
                mimeType = "text/plain";
                break;
            case "gif":
            case "bmp":
            case "png":
                mimeType = `image/${ext}`;
                break;
            case "jpg":
            case "jpeg":
                mimeType = `image/jpeg`;
                break;
            case "html":
                mimeType = `text/html`;
                break;
            case "IIF":
            case "QIF":
            case "asc":
            case "pft":
                mimeType = "text";
                break;
            case "csv":
                mimeType = "application/vnd.ms-excel";
                break;
            case "xlsx":
                mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                break;
            case "xdp":
                mimeType = "application/vnd.adobe.xdp+xml";
                break;
            case "docx":
                mimeType = "application/docx";
                break;
            case "tif":
                mimeType = "image/tiff";
                break;
            case "zip":
                mimeType = "application/zip";
                break;
            default:
                mimeType = "application/octet-stream";
                break;

        }
        return mimeType;
      
    }

    abstract copyTo(newParent?: FolderEntry, newName?: string): Promise<FolderEntry | FileEntry>;
	abstract moveTo(newParent: FolderEntry, newName?: string): Promise<FolderEntry | FileEntry>
    abstract remove(): Promise<boolean>;
    
	abstract getParent(): Promise<FolderEntry>;
	// just in case standarded fields cannot get it when GetFile
//	abstract getMetadata?(selectFields?: [string]): Promise<EntryMetadata>;
	abstract getTags(): Promise<Array<string>>;
	abstract addTags(tags: string | Array<string>): Promise<void>;
	// remove all if no tags param
	abstract removeTags(tags?: string | Array<string>): Promise<void>;
    abstract getPureMeta(): Promise<PureMetadata>;
	// no name param  get all
	abstract getExtendedFields(names?: string | Array<string>): Promise<ExtendedField|Array<ExtendedField>>;
	abstract updateExtendedFields(fld: ExtendedField | Array<ExtendedField>): Promise<void>;

	abstract getDownloadUrl(): Promise<string>;
    abstract getShareableUrl(who?:string, expire?:Date): Promise<string>;
    
    abstract buildSubPath(): string;

    abstract buildFullPath(): string;
}

export abstract class FileEntry extends BaseEntry {
 	abstract getContent(type: 'blob' | 'arrayBuffer' | string | 'xml', convertToFormat?: string): Promise<Blob | ArrayBuffer | string | XMLDocument>;
	abstract downloadFile(): Promise<void>;
    abstract getHistories?(): Promise<Array<EntryMetadata>>;
    abstract getHistoryContent?(version: string, type?: 'blob' | 'arrayBuffer' | 'xml' | string): Promise<Blob | ArrayBuffer | string>;
	abstract revertToHistory?(version: string): Promise<boolean>;
	// if no startPos, new content start from 0
	// if no endPos it will cut off file after the new content ends
    abstract updateContent(content: Blob | ArrayBuffer | string, startPos?: number, endPos?: number): Promise<void>;
    abstract openDocument(readonly: boolean, desktopWord: boolean): Promise<void>;
    abstract openDocumentAsPDF(): Promise<void>;
    abstract getGraphUrlByID(): Promise<string>;
}

export interface FileSystemOptions {
    create?: boolean;
    overwrite?: boolean;
    //exclusive?: boolean; // if create is true, it will fail if file/folder already exist
}

export interface ArchiveData {
    filePath: string;
    fileDownloadUrl: string;
}

export type TreeNode = {
    name: string;
    children?: TreeNode[];
    fileUrl?: string;
}

export abstract class FolderEntry extends BaseEntry {
	// may support wild card filespec 
	abstract getContentList(filespec?: string): Promise<Array<FileEntry | FolderEntry>>;
	// name can be under sub directory like  '456/uploaded/driverlic.png'
	// there is not no reason not support absulate path as well like '/client files/2019/02/M43458/inital letter.docx' 
	// but logiclly not good idea, it make the code not easy to read
	// create if do not exist when pass {create:true} option, it should be able to create sub directory as well
	abstract getFolder(name: string, options?: FileSystemOptions): Promise<FolderEntry>;
	abstract getFileByID(id: string): Promise<FileEntry>;
	abstract getFile(name: string, options?: FileSystemOptions, fileContent?: string | Blob | ArrayBuffer): Promise<FileEntry>;
}

export class FileSystem {
    config: FileSystemConfig;
    root: FolderEntry;   
	protected constructor(config: FileSystemConfig) {
        //console.log('setting ' + JSON.stringify(config))
		this.config = config;
		this.root = null;
        //console.log('set here '+ JSON.stringify(this.config));
	}
}

export interface FileSystemConfig {
    rootDrive: string;
    fileUrl?: string;
    startFolder?: string;
    host: string;
    AppID: string,
    AppCode:string,
    UserID:string     
}